export type ApprovalExpirationInterval = {
	value: number;
	unit: 'WEEK' | 'MONTH' | 'YEAR';
};

export type ApprovalExpirationAction =
	| { type: 'remove-status' }
	| { type: 'change-status'; statusName: string }
	| { type: 'restart-approval' }
	| { type: 'generic' };

export type ApprovalExpirationConfig = {
	interval: ApprovalExpirationInterval;
	action: ApprovalExpirationAction;
};

type RawApprovalExpirationConfig = {
	readonly isEnabled?: boolean | null;
	readonly interval?: {
		readonly value?: unknown;
		readonly unit?: unknown;
	} | null;
	readonly restartApproval?: boolean | null;
	readonly changeStatus?: {
		readonly isEnabled?: boolean | null;
		readonly expireToContentStateId?: string | null;
	} | null;
};

type ContentState = {
	readonly id?: string | null;
	readonly name?: string | null;
};

// The backend persists this sentinel when expiration should remove the page status.
// Keep this local: @confluence/content-state depends on this approvals package.
const REMOVE_STATUS_CONTENT_STATE_ID = '-1';

export type ApprovalExpirationConfigNormalizationResult = {
	config: ApprovalExpirationConfig | null;
	invalidConfigSignature: string | null;
};

const isApprovalExpirationIntervalUnit = (
	unit: unknown,
): unit is ApprovalExpirationInterval['unit'] =>
	unit === 'WEEK' || unit === 'MONTH' || unit === 'YEAR';

const getInvalidConfigSignature = (interval: RawApprovalExpirationConfig['interval']): string =>
	`${typeof interval?.value}:${String(interval?.value)}:${typeof interval?.unit}:${String(
		interval?.unit,
	)}`;

const getApprovalExpirationAction = (
	config: RawApprovalExpirationConfig,
	contentStates: ReadonlyArray<ContentState | null | undefined> | null | undefined,
): ApprovalExpirationAction => {
	const isChangeStatusEnabled = config.changeStatus?.isEnabled === true;
	const isRestartApprovalEnabled = config.restartApproval === true;

	if (isChangeStatusEnabled === isRestartApprovalEnabled) {
		return { type: 'generic' };
	}

	if (isRestartApprovalEnabled) {
		return { type: 'restart-approval' };
	}

	const targetContentStateId = config.changeStatus?.expireToContentStateId;
	if (targetContentStateId == null || targetContentStateId === REMOVE_STATUS_CONTENT_STATE_ID) {
		return { type: 'remove-status' };
	}

	const matchingContentStates = (contentStates ?? []).filter(
		(contentState) => contentState?.id === targetContentStateId,
	);
	const statusName = matchingContentStates[0]?.name?.trim();

	if (matchingContentStates.length !== 1 || !statusName) {
		return { type: 'generic' };
	}

	return { type: 'change-status', statusName };
};

export const normalizeApprovalExpirationConfig = (
	config: RawApprovalExpirationConfig | null | undefined,
	contentStates: ReadonlyArray<ContentState | null | undefined> | null | undefined,
): ApprovalExpirationConfigNormalizationResult => {
	if (config?.isEnabled !== true) {
		return { config: null, invalidConfigSignature: null };
	}

	const interval = config.interval;
	const intervalValue = interval?.value;
	if (
		!interval ||
		typeof intervalValue !== 'number' ||
		!Number.isSafeInteger(intervalValue) ||
		intervalValue <= 0 ||
		!isApprovalExpirationIntervalUnit(interval.unit)
	) {
		return {
			config: null,
			invalidConfigSignature: getInvalidConfigSignature(interval),
		};
	}

	return {
		config: {
			interval: { value: intervalValue, unit: interval.unit },
			action: getApprovalExpirationAction(config, contentStates),
		},
		invalidConfigSignature: null,
	};
};
